import React, { useState, useEffect } from 'react';
import { X, Mail, Phone, MapPin, Loader2, User, Search, SlidersHorizontal } from 'lucide-react';
import { fetchRentingPreferenceMatches } from '../api/analyticsApi';
import RentingPreferencesModal from '../../contacts/components/RentingPreferencesModal';

const FILTER_LABELS = {
    suburb: 'Suburb',
    city: 'City',
    secondary_suburb: 'Secondary Suburb',
    property_type: 'Property Type',
    bedrooms: 'Max. Bedrooms',
    rent_max: 'Weekly Rent (Max)',
    car_spaces: 'Car Spaces',
    move_in_timeframe: 'Move-in Timeframe',
    pets: 'Pets',
    occupants: 'Occupants',
    all: 'All renters',
};

// The renter counterpart of BuyerPreferenceMatchesModal.js — same drill-down
// shape, trimmed to the fields RentingPreference has (no timeline/finance/
// buyer-type columns) and opening RentingPreferencesModal instead of
// BuyerPreferencesModal for the inline "Preferences" edit button.
const RenterPreferenceMatchesModal = ({ isOpen, onClose, filterType, filterValue, startDate, endDate, onRenterClick }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [results, setResults] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    // Renter whose renting preferences are being edited (null = closed).
    const [prefsIdentity, setPrefsIdentity] = useState(null);

    useEffect(() => {
        if (!isOpen || !filterType || filterValue == null) return;
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                setSearchTerm('');
                const data = await fetchRentingPreferenceMatches(filterType, String(filterValue), startDate, endDate);
                if (!cancelled) setResults(data?.results || []);
            } catch (err) {
                if (!cancelled) setError('Failed to load renters for this filter');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isOpen, filterType, filterValue, startDate, endDate]);

    if (!isOpen) return null;

    const filtered = results.filter((r) => {
        if (!searchTerm) return true;
        const s = searchTerm.toLowerCase();
        return (
            r.buyer_name?.toLowerCase().includes(s) ||
            r.buyer_email?.toLowerCase().includes(s) ||
            r.buyer_phone?.toLowerCase().includes(s) ||
            r.session_id?.toLowerCase().includes(s)
        );
    });

    const filterLabel = FILTER_LABELS[filterType] || filterType;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">
                            {filterType === 'all' ? 'All renters' : 'Renters matching preference'}
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                            {filterType === 'all' ? (
                                <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-bold uppercase tracking-wider">
                                    {startDate || endDate ? 'In selected range' : 'All time'}
                                </span>
                            ) : (
                                <>
                                    <span className="font-semibold text-gray-700">{filterLabel}:</span>{' '}
                                    <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-bold">
                                        {String(filterValue)}
                                    </span>
                                </>
                            )}
                            <span className="ml-2 text-gray-500">
                                ({filtered.length} {filtered.length === 1 ? 'renter' : 'renters'})
                            </span>
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-6 h-6 text-gray-500" />
                    </button>
                </div>

                <div className="px-6 pt-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name, email, phone..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                        </div>
                    ) : error ? (
                        <div className="text-center text-red-600 py-8">{error}</div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center text-gray-500 py-8">
                            No renters match this preference.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Renter</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Suburbs</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rent / Beds</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filtered.map((r) => {
                                        const clickable = (!!r.buyer_id || !!r.contact_id) && !!onRenterClick;
                                        const onRowClick = () => {
                                            if (!clickable) return;
                                            onRenterClick({
                                                buyer_id: r.buyer_id,
                                                contact_id: r.contact_id,
                                                buyer_name: r.buyer_name,
                                                buyer_email: r.buyer_email,
                                            });
                                        };
                                        return (
                                            <tr
                                                key={r.preference_id}
                                                className={`transition-colors ${clickable ? 'hover:bg-gray-50 cursor-pointer' : ''}`}
                                                onClick={onRowClick}
                                            >
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div className="flex-shrink-0 h-9 w-9 bg-blue-50 rounded-full flex items-center justify-center">
                                                            <User className="w-4 h-4 text-blue-600" />
                                                        </div>
                                                        <div className="ml-3">
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {r.buyer_name || (r.is_anonymous ? 'Anonymous visitor' : r.buyer_email || '—')}
                                                            </div>
                                                            {r.is_anonymous ? (
                                                                <div className="text-xs text-gray-400">Session {r.session_id?.slice(0, 8)}…</div>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setPrefsIdentity({
                                                                            contactId: r.contact_id,
                                                                            buyerId: r.buyer_id,
                                                                            email: r.buyer_email,
                                                                            name: r.buyer_name,
                                                                            phone: r.buyer_phone,
                                                                        });
                                                                    }}
                                                                    title="Edit this renter's renting preferences"
                                                                    className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-teal-300 hover:text-teal-600"
                                                                >
                                                                    <SlidersHorizontal className="h-3.5 w-3.5" /> Preferences
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                    {r.buyer_email && (
                                                        <div className="flex items-center gap-1.5">
                                                            <Mail className="w-3.5 h-3.5 text-gray-400" />
                                                            {r.buyer_email}
                                                        </div>
                                                    )}
                                                    {r.buyer_phone && (
                                                        <div className="flex items-center gap-1.5 text-gray-500 text-xs mt-0.5">
                                                            <Phone className="w-3.5 h-3.5 text-gray-400" />
                                                            {r.buyer_phone}
                                                        </div>
                                                    )}
                                                    {!r.buyer_email && !r.buyer_phone && (
                                                        <span className="text-gray-400 text-xs">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700">
                                                    {r.suburbs?.length ? (
                                                        <div className="flex items-start gap-1.5">
                                                            <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                                                            <span className="line-clamp-2">{r.suburbs.join(', ')}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400 text-xs">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                                    <div>{[r.rent_min, r.rent_max].filter((v) => v != null && v !== 'Any').join(' – ') || 'Any'}/wk</div>
                                                    <div className="text-xs text-gray-500">{r.bedrooms_max ?? r.bedrooms_min} bed</div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end p-4 border-t border-gray-200 bg-gray-50">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
            {prefsIdentity && (
                <RentingPreferencesModal
                    identity={prefsIdentity}
                    title={prefsIdentity.name}
                    onClose={() => setPrefsIdentity(null)}
                />
            )}
        </div>
    );
};

export default RenterPreferenceMatchesModal;
